import getDirectStepAttributes from './getDirectStepAttributes';

/**
 * Resolves the chain of iterate steps to find the original data source and collect all dataKeys.
 * Returns { originalStepId, dataKeyChain } where dataKeyChain is an array of dataKeys from outer to inner.
 */
const resolveIterateChainForAttributes = (stepId, steps, apps) => {
  const dataKeyChain = [];
  let currentStepId = stepId;

  while (currentStepId) {
    const step = steps?.find((s) => s.id === currentStepId);
    if (!step) {
      break;
    }

    const app = apps?.find((a) => a.id === step.appId);
    const isIterate = app?.slug === 'iterate';

    if (isIterate) {
      // Add this iterate step's dataKey to the chain
      if (step.arguments?.dataKey) {
        dataKeyChain.push(step.arguments.dataKey);
      }
      currentStepId = step.arguments?.stepId;
    } else {
      // Found the original non-iterate data source
      return { originalStepId: currentStepId, dataKeyChain };
    }
  }

  return { originalStepId: null, dataKeyChain };
};

/**
 * Filters attributes by applying a chain of dataKeys.
 * Each dataKey in the chain filters and transforms the attribute keys.
 */
const filterAttributesByDataKeyChain = (attributes, dataKeyChain) => {
  let filtered = [...attributes];

  for (const dataKey of dataKeyChain) {
    if (!dataKey) {
      continue;
    }

    // Filter to only attributes that start with this dataKey
    // and transform the keys by removing the dataKey prefix
    filtered = filtered
      .filter((attr) => attr?.key?.startsWith(dataKey) && attr?.key !== `${dataKey}[]`)
      .map((attr) => ({
        ...attr,
        key: attr?.key?.replace(`${dataKey}[].`, '')
      }));
  }

  return filtered;
};

const getIteratorAttributes = ({ stepId, apps, steps }) => {
  const step = steps?.find((n) => n?.id === stepId);
  const referenceStepId = step?.arguments?.stepId;
  const referenceStep = steps?.find((n) => n?.id === referenceStepId);
  const referenceApp = apps?.find((a) => a?.id === referenceStep?.appId);

  // Check if the reference step is also an iterate step (nested loops)
  const isNestedIterate = referenceApp?.slug === 'iterate';

  if (isNestedIterate) {
    // Resolve the full chain to get the original data source
    const { originalStepId, dataKeyChain: rawChain } = resolveIterateChainForAttributes(stepId, steps, apps);
    // Reverse to get outer-to-inner order for filtering
    const dataKeyChain = rawChain.reverse();

    if (!originalStepId) {
      return [];
    }

    const originalStep = steps?.find((s) => s.id === originalStepId);
    const originalAppId = originalStep?.appId;

    // Get attributes from the original data source (no parent filtering - we'll do it ourselves)
    const originalAttributes = getDirectStepAttributes({
      stepId: originalStepId,
      appId: originalAppId,
      steps,
      apps
    });

    // Apply the chain of dataKey filters
    return filterAttributesByDataKeyChain(originalAttributes, dataKeyChain);
  }

  // Not nested - use the original single-level logic
  const appId = referenceStep?.appId;
  return getDirectStepAttributes({ parentStepId: stepId, stepId: referenceStep?.id, appId, steps, apps });
};

export default getIteratorAttributes;
